import { useState, type FormEvent } from 'react';
import { ShieldAlert, AlertCircle } from 'lucide-react';
import { useEmergencyLogin } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';
import { ApiError } from '../../lib/api';

/**
 * Emergency admin bypass login (CLAUDE.md §6.5). Reached only by
 * direct URL.
 */
export function EmergencyLogin() {
  const emergencyLogin = useEmergencyLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await emergencyLogin.mutateAsync({ email, password });
      window.location.href = '/';
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.status === 404 ? 'Emergency login is not enabled' : err.message);
      } else {
        setError('emergency login failed');
      }
    }
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-6 text-fg">
      <div className="mx-auto mt-12 max-w-md sm:mt-20">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-600 text-white shadow-lg">
            <ShieldAlert className="h-7 w-7" strokeWidth={2} />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Emergency bypass</h1>
          <p className="mt-1 text-sm text-fg-muted">
            Use only when Entra ID is unavailable.
          </p>
        </div>

        <div className="overflow-hidden rounded-2xl border-2 border-red-300 bg-surface shadow-xl">
          <div className="bg-red-600 px-4 py-2.5 text-center text-sm font-semibold text-white">
            All actions under this session are audited
          </div>

          <div className="space-y-4 p-6 sm:p-8">
            <div className="rounded-md bg-danger-soft/15 px-3 py-2 text-xs text-danger-soft-fg">
              Every action is recorded in the dedicated{' '}
              <code className="rounded bg-danger-soft/15 px-1">emergency_access</code>{' '}
              audit log. A post-incident review is mandatory. Session cap: 4 hours.
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <label className="block text-sm">
                <span className="font-medium text-fg-muted">Username</span>
                <input
                  type="text"
                  required
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 block h-11 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-fg-muted">Password</span>
                <input
                  type="password"
                  required
                  autoComplete="off"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 block h-11 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
                />
              </label>

              {error ? (
                <div className="flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              ) : null}

              <Button
                type="submit"
                variant="destructive"
                size="lg"
                fullWidth
                loading={emergencyLogin.isPending}
              >
                Authenticate
              </Button>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}
